import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { getAdminCategories } from "../../../../services/Admin/ProductsAndCategoriesApi";

const emptyProduct = () => ({
  category_id: "",
  name_ar: "",
  name_en: "",
  desc_ar: "",
  desc_en: "",
  main_photo: null,
  quantity: "",
  return_policie_ar: null,
  return_policie_en: null,
  discount: 0,
  retrun_status: false,
  sale: false,
  status: true,
  images: [], // { title_ar, title_en, imageFile (optional) }
  sizes: [], // { key, size }
  colors: [], // { color_ar, color_en, code }
  features: [], // { title_ar, title_en, desc_ar, desc_en }
  currencies: [], // { country_id, price, price_after_discount }
});

const isActiveCategory = (status) => status === 1 || status === "1" || status === true || status === "true";

// Build selectable categories tree where inactive parents are hidden,
// but active descendants are still included.
const flattenSelectableCategories = (cats, level = 0) => {
  if (!Array.isArray(cats)) return [];

  let result = [];
  cats.forEach((cat) => {
    const children = Array.isArray(cat.children_recursive) ? cat.children_recursive : [];
    const currentIsActive = isActiveCategory(cat?.status);

    if (currentIsActive) {
      result.push({ ...cat, level });
      result = result.concat(flattenSelectableCategories(children, level + 1));
    } else {
      // Parent is inactive: skip it, but still surface active descendants.
      result = result.concat(flattenSelectableCategories(children, level));
    }
  });
  return result;
};

const AddProductsDialog = ({ open, onClose, onSubmit }) => {
  const [products, setProducts] = useState([emptyProduct()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchCategories = async () => {
        try {
          setLoadingCategories(true);
          const res = await getAdminCategories();
          const data = res?.data || res || [];
          const flattened = flattenSelectableCategories(Array.isArray(data) ? data : []);
          setCategories(flattened);
        } catch (err) {
          console.error("Error fetching categories:", err);
          setCategories([]);
        } finally {
          setLoadingCategories(false);
        }
      };
      fetchCategories();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setProducts([emptyProduct()]);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const updateProduct = (idx, key, value) => {
    setProducts((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  const addProduct = () => setProducts((p) => [...p, emptyProduct()]);
  const removeProduct = (idx) => setProducts((p) => p.filter((_, i) => i !== idx));

  const validateProduct = (prod) => {
    if (!prod.name_ar && !prod.name_en) return "Each product needs name_ar or name_en";
    // إذا كانت currencies موجودة وليست فارغة، فاعمل تحقق عليها (country_id و price مطلوبان)
    if (prod.currencies && Array.isArray(prod.currencies) && prod.currencies.length > 0) {
      for (let i = 0; i < prod.currencies.length; i++) {
        const cur = prod.currencies[i] || {};
        if (!cur.country_id || cur.country_id === "" || cur.price == null || cur.price === "") {
          return `Product currency at index ${i} is missing country_id or price`;
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    setError(null);
    // basic validation
    for (let i = 0; i < products.length; i++) {
      const err = validateProduct(products[i]);
      if (err) {
        setError(`Product ${i + 1}: ${err}`);
        return;
      }
    }

    const formData = new FormData();

    const appendValue = (key, value) => {
      if (value === undefined || value === null || value === "") return;
      formData.append(key, String(value));
    };

    products.forEach((p, idx) => {
      appendValue(`products[${idx}][category_id]`, p.category_id ? Number(p.category_id) : null);
      appendValue(`products[${idx}][name_ar]`, p.name_ar || null);
      appendValue(`products[${idx}][name_en]`, p.name_en || null);
      appendValue(`products[${idx}][desc_ar]`, p.desc_ar || null);
      appendValue(`products[${idx}][desc_en]`, p.desc_en || null);
      appendValue(`products[${idx}][quantity]`, p.quantity ? Number(p.quantity) : 0);
      appendValue(`products[${idx}][return_policie_ar]`, p.return_policie_ar);
      appendValue(`products[${idx}][return_policie_en]`, p.return_policie_en);
      appendValue(`products[${idx}][discount]`, p.discount ? Number(p.discount) : 0);
      appendValue(`products[${idx}][retrun_status]`, p.retrun_status ? 1 : 0);
      appendValue(`products[${idx}][sale]`, p.sale ? 1 : 0);
      appendValue(`products[${idx}][status]`, p.status ? 1 : 0);

      if (p.main_photo instanceof File) {
        formData.append(`products[${idx}][main_photo]`, p.main_photo);
      }

      (p.images || []).forEach((im, imIdx) => {
        appendValue(`products[${idx}][images][${imIdx}][title_ar]`, im.title_ar || null);
        appendValue(`products[${idx}][images][${imIdx}][title_en]`, im.title_en || null);
        appendValue(`products[${idx}][images][${imIdx}][image]`, im.image_base64 || null);
        appendValue(`products[${idx}][images][${imIdx}][image_name]`, im.image_name || null);
      });

      (p.sizes || []).forEach((s, sIdx) => {
        appendValue(`products[${idx}][sizes][${sIdx}][key]`, s.key || null);
        appendValue(`products[${idx}][sizes][${sIdx}][size]`, s.size || null);
      });

      (p.colors || []).forEach((c, cIdx) => {
        appendValue(`products[${idx}][colors][${cIdx}][color_ar]`, c.color_ar || null);
        appendValue(`products[${idx}][colors][${cIdx}][color_en]`, c.color_en || null);
        appendValue(`products[${idx}][colors][${cIdx}][code]`, c.code || null);
      });

      (p.features || []).forEach((f, fIdx) => {
        appendValue(`products[${idx}][features][${fIdx}][title_ar]`, f.title_ar || null);
        appendValue(`products[${idx}][features][${fIdx}][title_en]`, f.title_en || null);
        appendValue(`products[${idx}][features][${fIdx}][desc_ar]`, f.desc_ar || null);
        appendValue(`products[${idx}][features][${fIdx}][desc_en]`, f.desc_en || null);
      });

      (p.currencies || []).forEach((cur, curIdx) => {
        appendValue(`products[${idx}][currencies][${curIdx}][country_id]`, cur.country_id ? Number(cur.country_id) : null);
        appendValue(`products[${idx}][currencies][${curIdx}][price]`, cur.price ? Number(cur.price) : 0);
        appendValue(`products[${idx}][currencies][${curIdx}][price_after_discount]`, cur.price_after_discount ? Number(cur.price_after_discount) : 0);
      });
    });

    try {
      setSubmitting(true);
      await onSubmit(formData);
      onClose && onClose();
    } catch (err) {
      const serverData = err?.response?.data || err?.original?.response?.data;
      if (serverData) {
        const msgs = [];
        if (serverData.message) msgs.push(serverData.message);
        if (serverData.errors && typeof serverData.errors === "object") {
          Object.values(serverData.errors).forEach((v) => {
            if (Array.isArray(v)) msgs.push(...v);
            else msgs.push(v);
          });
        }
        setError(msgs.length ? msgs.join(" | ") : (err?.message || "Error"));
      } else {
        setError(err?.message || "Error saving products");
      }
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose && onClose()} maxWidth="lg" fullWidth>
      <DialogTitle>إضافة منتجات</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
          {products.map((p, idx) => (
            <Paper key={idx} sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>المنتج #{idx + 1}</Typography>
                <Box>
                  <IconButton size="small" onClick={() => addProduct()}><AddIcon /></IconButton>
                  {products.length > 1 && (
                    <IconButton size="small" onClick={() => removeProduct(idx)}><RemoveIcon /></IconButton>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "grid", gap: 1 }}>
                {/* Category Select */}
                <FormControl fullWidth>
                  <InputLabel>الفئة</InputLabel>
                  <Select
                    value={p.category_id}
                    onChange={(e) => updateProduct(idx, "category_id", e.target.value)}
                    label="الفئة"
                    disabled={loadingCategories}
                  >
                    <MenuItem value="">
                      <em>-- اختر الفئة --</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <span style={{ marginLeft: cat.level * 20 }}>
                            {cat.level > 0 && "└─ "}
                            {cat.name_ar || cat.name_en}
                          </span>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: "text.secondary", ml: 1 }}
                          >
                            ({cat.name_en})
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingCategories && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        جاري تحميل الفئات...
                      </Typography>
                    </Box>
                  )}
                </FormControl>

                <TextField label="الاسم بالعربي" value={p.name_ar} onChange={(e) => updateProduct(idx, "name_ar", e.target.value)} />
                <TextField label="الاسم بالإنجليزي" value={p.name_en} onChange={(e) => updateProduct(idx, "name_en", e.target.value)} />
                <TextField label="الوصف بالعربي" value={p.desc_ar} onChange={(e) => updateProduct(idx, "desc_ar", e.target.value)} multiline />
                <TextField label="الوصف بالإنجليزي" value={p.desc_en} onChange={(e) => updateProduct(idx, "desc_en", e.target.value)} multiline />
                <Box>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => updateProduct(idx, "main_photo", e.target.files?.[0] || null)}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    الصورة الرئيسية
                    {p.main_photo ? `: ${p.main_photo.name}` : ""}
                  </Typography>
                </Box>
                <TextField label="الكمية" value={p.quantity} onChange={(e) => updateProduct(idx, "quantity", e.target.value)} />
                {/* <TextField label="الخصم" value={p.discount} onChange={(e) => updateProduct(idx, "discount", e.target.value)} /> */}
                <FormControlLabel control={<Checkbox checked={p.retrun_status} onChange={(e) => updateProduct(idx, "retrun_status", e.target.checked)} />} label="حالة يقبل المرتجع؟" />
                
                {p.retrun_status && (
                  <>
                    <TextField
                      label="سياسة الاسترجاع (عربي)"
                      value={p.return_policie_ar}
                      onChange={(e) => updateProduct(idx, "return_policie_ar", e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="أدخل سياسة الاسترجاع بالعربية"
                    />
                    <TextField
                      label="سياسة الاسترجاع (إنجليزي)"
                      value={p.return_policie_en}
                      onChange={(e) => updateProduct(idx, "return_policie_en", e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Enter return policy in English"
                    />
                  </>
                )}

                <FormControlLabel control={<Checkbox checked={p.sale} onChange={(e) => updateProduct(idx, "sale", e.target.checked)} />} label="تخفيض" />
                <FormControlLabel control={<Checkbox checked={p.status} onChange={(e) => updateProduct(idx, "status", e.target.checked)} />} label="الحالة" />


              </Box>
            </Paper>
          ))}
          {error ? <Typography color="error">{error}</Typography> : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => !submitting && onClose && onClose()}>إلغاء</Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductsDialog;
